import { useState } from 'react';
import type { LobbyInfo } from '../../api/types';
import { GameModeNames, LobbyStateNames, ServerRegionNames } from '../../domain/enums';
import { describeSeriesLobby, type SeriesLobbyView } from '../../domain/series';
import { Badge, Button, Card, cx } from '../../ui';
import type { TrackedSeries } from '../series/tracker';
import LobbyBoard from './LobbyBoard';

function label(map: Record<number, string>, value: number | undefined, fallback = '—'): string {
  if (value === undefined) return fallback;
  return map[value] ?? String(value);
}

/**
 * The strip a series lobby gets instead of the manual controls. The score is
 * per side as the Dota lobby shows it; the API keeps the score per team and
 * re-labels the sides whenever the coin toss swaps them.
 */
function SeriesStrip({ series, tracked }: { series: SeriesLobbyView; tracked: TrackedSeries | null }) {
  return (
    <div className="mt-4 rounded-lg border border-gold-400/40 bg-gold-400/10 p-3 text-xs">
      <p className="font-semibold text-gold-300">
        {series.formatLabel} · Game {series.gameNumber} of {series.maxGames} · Radiant{' '}
        {series.radiantWins} – {series.direWins} Dire
      </p>
      {tracked && (
        <p className="mt-1 text-ink-dim">
          <span className="text-ok">{tracked.radiantTeam || 'Radiant team'}</span>
          {' vs '}
          <span className="text-ember-400">{tracked.direTeam || 'Dire team'}</span>
        </p>
      )}
      <p className="mt-1 text-ink-dim">
        Runs by itself: players type <code className="text-ink">!ready</code> in lobby chat, the bot
        launches each game and opens the next lobby when it ends.
      </p>
      {!series.exact && (
        <p className="mt-1 text-ink-faint">
          Format as reported by Dota. A best of 2 started elsewhere also shows as best of 3.
        </p>
      )}
    </div>
  );
}

export type LobbyCardActions = {
  onManageSeries?: (lobby: LobbyInfo) => void;
  onInvite: (lobby: LobbyInfo) => void;
  onChat: (lobby: LobbyInfo) => void;
  onEdit: (lobby: LobbyInfo) => void;
  onDuplicate?: (lobby: LobbyInfo) => void;
  onLaunch: (lobby: LobbyInfo) => void;
  onDestroy: (lobby: LobbyInfo) => void;
};

export default function LobbyCard({
  lobby,
  tracked = null,
  actions,
  busyAction,
}: {
  lobby: LobbyInfo;
  /** The series this browser started on the lobby's bot, when there is one. */
  tracked?: TrackedSeries | null;
  actions: LobbyCardActions;
  busyAction?: 'launch' | 'destroy';
}) {
  const [manage, setManage] = useState(false);

  const series = describeSeriesLobby(lobby, tracked?.format ?? null);
  const automated = lobby.controller_type === 'series';

  return (
    <Card
      className="h-full"
      title={lobby.game_name || `Lobby ${lobby.lobby_id}`}
      description={`Bot ${lobby.bot_id}`}
      actions={
        <>
          {series && (
            <Badge tone="gold">
              {series.formatLabel} · Game {series.gameNumber}
            </Badge>
          )}
          <Badge tone="neutral">{label(ServerRegionNames, lobby.server_region)}</Badge>
          <Badge tone={series ? 'neutral' : 'gold'}>{label(GameModeNames, lobby.game_mode)}</Badge>
          {lobby.has_password && <Badge tone="neutral">🔒</Badge>}
          {lobby.state !== undefined && (
            <Badge tone="info">{label(LobbyStateNames, lobby.state)}</Badge>
          )}
        </>
      }
    >
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs sm:grid-cols-4">
        <div>
          <dt className="text-ink-faint">Players seated</dt>
          <dd className="text-ink">{lobby.seated_count ?? lobby.player_count}</dd>
        </div>
        <div>
          <dt className="text-ink-faint">Lobby ID</dt>
          <dd className="truncate font-mono text-ink" title={String(lobby.lobby_id)}>
            {lobby.lobby_id}
          </dd>
        </div>
        <div>
          <dt className="text-ink-faint">League</dt>
          <dd className="text-ink">{lobby.league_id || '—'}</dd>
        </div>
        <div>
          <dt className="text-ink-faint">Series</dt>
          <dd className="text-ink">
            {series ? `${series.formatLabel} · ${series.radiantWins}-${series.direWins}` : '—'}
          </dd>
        </div>
      </dl>

      {automated && series && <SeriesStrip series={series} tracked={tracked} />}

      <div className="mt-4 rounded-lg border border-line bg-surface-2/40">
        <button
          type="button"
          onClick={() => setManage((open) => !open)}
          aria-expanded={manage}
          className={cx(
            'flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-ink transition-colors hover:bg-surface-2',
            manage && 'rounded-b-none border-b border-line',
          )}
        >
          <span className="flex items-center gap-2">
            <span aria-hidden="true" className="text-xs text-ink-faint">
              {manage ? '▾' : '▸'}
            </span>
            Seats and players
            <Badge tone="neutral">
              {lobby.player_count} {lobby.player_count === 1 ? 'person' : 'people'}, bot aside
            </Badge>
          </span>
          <span className="text-xs text-ink-dim">
            {manage ? 'Hide' : 'Swap sides, kick, seat the bot, AI bots'}
          </span>
        </button>
        {manage && (
          <div className="p-3">
            <LobbyBoard lobby={lobby} series={automated} />
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {actions.onDuplicate && <Button size="sm" onClick={() => actions.onDuplicate?.(lobby)}>Duplicate as new</Button>}
        <Button size="sm" onClick={() => actions.onInvite(lobby)}>
          Invite
        </Button>
        <Button size="sm" onClick={() => actions.onChat(lobby)}>
          Chat
        </Button>
        {automated ? (
          <Button
            size="sm"
            onClick={() => actions.onManageSeries?.(lobby)}
          >
            Manage series
          </Button>
        ) : (
          <>
            <Button size="sm" onClick={() => actions.onEdit(lobby)}>
              Settings
            </Button>
            <Button
              size="sm"
              variant="primary"
              loading={busyAction === 'launch'}
              onClick={() => actions.onLaunch(lobby)}
            >
              Launch
            </Button>
            <Button
              size="sm"
              variant="danger"
              loading={busyAction === 'destroy'}
              onClick={() => actions.onDestroy(lobby)}
            >
              Destroy
            </Button>
          </>
        )}
      </div>
    </Card>
  );
}
